import { useCallback, useEffect, useState, useMemo } from 'react';
import { Button, Select, Form } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { produce } from 'immer';
import styled from '@emotion/styled';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import TimeGroup from './TimeGroup';
import type { TimeRangeItem } from './TimeGroup';
import { useLatest } from 'ahooks';
import { useTranslate } from '@tolgee/react';

export type DayOfWeeks = (
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY'
)[];

type WeekValue = {
  dayOfWeeks: DayOfWeeks;
  timeRanges: Pick<TimeRangeItem, 'startAt' | 'endAt'>[];
};

type WeeKGroupProps = {
  value?: WeekValue[];
  onChange?: (value: WeekValue[]) => void;
};

type WeekDayTimeRange = {
  id: string;
  value: WeekValue;
};

export const WeekValuable = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 0,
};

function newRange(defaultValue: boolean = false) {
  let dayOfWeeks: DayOfWeeks = [];
  if (defaultValue) {
    dayOfWeeks = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  }
  return { id: uuidv4(), value: { dayOfWeeks, timeRanges: [] } };
}

function WeeKGroup(props: WeeKGroupProps) {
  const { value, onChange } = props;
  const onChangeRef = useLatest(onChange);
  const { t } = useTranslate();
  const [items, setItems] = useState<WeekDayTimeRange[]>(() => {
    if (value && value?.length > 0) {
      return value.map((each) => {
        return {
          id: uuidv4(),
          value: {
            dayOfWeeks: each.dayOfWeeks,
            timeRanges: each.timeRanges.map((each) => {
              return {
                startAt: each.startAt,
                endAt: each.endAt,
              };
            }),
          },
        };
      });
    } else {
      return [newRange(true)];
    }
  });
  const itemsRef = useLatest(items);

  const addItem = () => {
    if (items.length < 4) {
      setItems(
        produce((draft) => {
          draft.push(newRange());
        }),
      );
    }
  };

  const deleteItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter((each) => each.id !== id));
    }
  };

  const updateWeekday = (id: string, value: DayOfWeeks) => {
    setItems(
      produce((draft) => {
        const filter = draft.filter((each) => each.id === id);
        const notFilter = draft.filter((each) => each.id !== id);

        filter[0].value.dayOfWeeks = value.sort((a, b) => WeekValuable[a] - WeekValuable[b]);
        notFilter.forEach((each) => {
          each.value.dayOfWeeks = each.value.dayOfWeeks.filter((each) => !value.includes(each));
        });
      }),
    );
  };

  const handleTimeChange = useCallback((value, id) => {
    setItems(
      produce((draft) => {
        const filter = draft.filter((each) => each.id === id);
        filter[0].value.timeRanges = value;
      }),
    );
  }, []);

  useEffect(() => {
    onChangeRef.current?.(items.map((each) => each.value));
  }, [items, onChangeRef]);

  return (
    <Container>
      {items.map((each, index) => {
        return (
          <Item key={each.id}>
            <div className="flex h-[40px] px-[16px] py-[10px] border-0 border-b-[#f0f2f5] border-b border-solid text-[#232426]">
              {t('multiTimeSelect.生效时间段')}
              {index + 1}
            </div>
            <Cancel onClick={() => deleteItem(each.id)} />
            <div className="p-[16px]">
              <Form.Item
                name={`i-dont-care-${each.id}`}
                label={t('multiTimeSelect.每周')}
                required
                rules={[
                  {
                    validateTrigger: ['onChange', 'onBlur'],
                    validator: () => {
                      const value = itemsRef.current.find((item) => item.id === each.id);
                      if (value?.value.dayOfWeeks.length === 0) {
                        return Promise.reject(t('multiTimeSelect.请选择星期'));
                      } else {
                        return Promise.resolve();
                      }
                    },
                  },
                ]}
              >
                <div>
                  <SelectWeekDay
                    value={each.value?.dayOfWeeks}
                    onChange={(value) => updateWeekday(each.id, value)}
                  />
                </div>
              </Form.Item>
              <TimeGroup
                value={each.value?.timeRanges}
                onChange={(value) => handleTimeChange(value, each.id)}
              />
            </div>
          </Item>
        );
      })}
      {items.length < 4 && (
        <Button type="link" style={{ padding: '0' }} onClick={addItem} icon={<PlusOutlined />}>
          {t('multiTimeSelect.新增时间段')}
        </Button>
      )}
    </Container>
  );
}

type SelectWeekDayProps = {
  value: DayOfWeeks;
  onChange: (value: DayOfWeeks) => void;
};

function SelectWeekDay(props: SelectWeekDayProps) {
  const { value, onChange } = props;
  const { t } = useTranslate();
  const weekDayOptions = useMemo(() => {
    return [
      { label: t('multiTimeSelect.周一'), value: 'MONDAY' },
      { label: t('multiTimeSelect.周二'), value: 'TUESDAY' },
      { label: t('multiTimeSelect.周三'), value: 'WEDNESDAY' },
      { label: t('multiTimeSelect.周四'), value: 'THURSDAY' },
      { label: t('multiTimeSelect.周五'), value: 'FRIDAY' },
      { label: t('multiTimeSelect.周六'), value: 'SATURDAY' },
      { label: t('multiTimeSelect.周日'), value: 'SUNDAY' },
    ];
  }, [t]);

  return (
    <Select
      mode="multiple"
      style={{ marginBottom: '8px', width: '100%' }}
      placeholder={t('multiTimeSelect.请选择星期')}
      value={value}
      onChange={onChange}
    >
      {weekDayOptions.map((each) => {
        return (
          <Select.Option key={each.value} value={each.value}>
            {each.label}
          </Select.Option>
        );
      })}
    </Select>
  );
}

const Container = styled.div``;

const Item = styled.div`
  position: relative;
  margin-bottom: 8px;
  background: #fff;
`;

const Cancel = styled(DeleteOutlined)`
  position: absolute;
  top: 8px;
  right: 10px;
  width: 24px;
  height: 24px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #84868c;
`;

export default WeeKGroup;
